import { useEffect, useState } from "react";
import LegalPage from "@/components/legal/LegalPage";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Style constants — mirrored verbatim from TermsOfService / PrivacyPolicy /
// RefundPolicy / SubscriptionTerms so this page renders identically inside
// the shared LegalPage shell. No new CSS, no new components, no layout tweaks.
const H2 = { color: "#f7f7f7", fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 10, letterSpacing: "0.2px" };
const P  = { margin: "0 0 12px 0" };
const P0 = { margin: "0 0 6px 0" };

function _fmtRemaining(iso) {
  if (!iso) return null;
  const end = new Date(iso).getTime();
  if (Number.isNaN(end)) return null;
  const diff = end - Date.now();
  if (diff <= 0) return { expired: true, text: null };
  const days  = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (days >= 1) return { expired: false, text: `${days} day${days === 1 ? "" : "s"} ${hours} hour${hours === 1 ? "" : "s"} remaining` };
  return { expired: false, text: `${hours} hour${hours === 1 ? "" : "s"} remaining` };
}

export default function MyAccount() {
  const { user } = useAuth();
  const [itemsCount, setItemsCount]     = useState(null);
  const [collectionsCount, setCollectionsCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: cnt }, { data: cols }] = await Promise.all([
          apiClient.get("/items/count"),
          apiClient.get("/collections"),
        ]);
        if (cancelled) return;
        setItemsCount(typeof cnt?.count === "number" ? cnt.count : 0);
        setCollectionsCount(Array.isArray(cols) ? cols.length : 0);
      } catch {
        if (cancelled) return;
        setItemsCount(0);
        setCollectionsCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const tier = user?.tier || "trial";
  const isTrial   = tier === "trial";
  const isBasic   = tier === "basic";
  const isPremium = tier === "premium";
  const planLabel = isPremium ? "Premium" : isBasic ? "Basic" : isTrial ? "Trial" : (tier || "Trial");

  const remaining = isTrial ? _fmtRemaining(user?.trial_expires_at) : null;

  return (
    <LegalPage title="My Account" testid="account-my" backTo="/home">
      <h2 style={H2}>Account Details</h2>
      <p style={P0}>Email: {user?.email || "—"}</p>
      {user?.trial_started_at ? (
        <p style={P}>Account created: {new Date(user.trial_started_at).toLocaleDateString()}</p>
      ) : null}

      <h2 style={H2}>Plan Status</h2>
      <p style={P}>{planLabel}</p>

      <h2 style={H2}>Trial Status</h2>
      {isTrial ? (
        remaining && remaining.expired
          ? <p style={P}>Your trial has expired.</p>
          : <p style={P}>{remaining?.text || "Trial active."}</p>
      ) : (
        <p style={P}>Your plan does not have a trial period.</p>
      )}

      <h2 style={H2}>Item Usage</h2>
      {isTrial ? (
        <p style={P}>{itemsCount == null ? "…" : `${itemsCount} of 12 items used`}</p>
      ) : (
        <p style={P}>Unlimited items</p>
      )}

      <h2 style={H2}>Collections</h2>
      <p style={P}>{collectionsCount == null ? "…" : `${collectionsCount} collection${collectionsCount === 1 ? "" : "s"}`}</p>

      <h2 style={H2}>Notes</h2>
      <p style={P}>This page provides an overview of your account details and plan status. You can manage your plan and upgrade options from the Plan &amp; Pricing page.</p>
    </LegalPage>
  );
}
